import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import type { StepParams } from '@enlighten/ts-shared/step-api';
import { filter, firstValueFrom } from 'rxjs';
import { SimpleAlertComponent, type SimpleAlertDialogData } from './simple-alert/simple-alert.component';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import { NotebookVariablesService } from '../notebook-variables/notebook-variables.service';
import { GeneralHelpers } from '../../helpers/general.helper';

export type SimpleAlertOptions = {
  message: string;
  details?: string;
  okText?: string;
  cancelText?: string;
  defaultInput?: string;
};

export const SimpleAlertOptionsDefaults: SimpleAlertOptions = {
  message: '',
  details: '',
  okText: 'OK',
  cancelText: 'Cancel',
  defaultInput: '',
};

@Injectable({
  providedIn: 'root'
})
export class SimpleUIService {
  private readonly dialog = inject(MatDialog);
  private readonly notebookVariablesService = inject(NotebookVariablesService);

  /**
   * Open the alert dialog and resolve with its close result. disableClose
   * ignores backdrop clicks; Escape is handled here and counts as Cancel.
   */
  private openAlertDialog(data: SimpleAlertDialogData): Promise<string | undefined> {
    const config: MatDialogConfig<SimpleAlertDialogData> = {
      data,
      disableClose: true,
      width: '500px',
      maxWidth: '95vw',
    };
    const dialogRef = this.dialog.open<SimpleAlertComponent, SimpleAlertDialogData, string | undefined>(
      SimpleAlertComponent,
      config,
    );
    dialogRef.keydownEvents()
      .pipe(filter(event => event.key === 'Escape'))
      .subscribe(() => dialogRef.close(undefined));
    return firstValueFrom(dialogRef.afterClosed());
  }

  /** Dialog options with the defaults filled in; an option left undefined keeps its default. */
  private static withDefaults(options: Partial<SimpleAlertOptions>): SimpleAlertOptions {
    const set = Object.fromEntries(
      Object.entries(options).filter(([, value]) => value !== undefined),
    ) as Partial<SimpleAlertOptions>;
    return { ...SimpleAlertOptionsDefaults, ...set };
  }

  /** `EHQ.showSimpleAlert`: resolves true on OK, false on Cancel. */
  public showSimpleAlert(
    params: StepParams['EHQ.showSimpleAlert'],
    context?: ChunkContext,
  ): Promise<boolean> {
    return this.confirm(params, context);
  }

  /**
   * Shows the alert dialog: true on OK, false on Cancel. Shared by
   * `showSimpleAlert` and other services (capturePhoto's prompt).
   */
  public async confirm(options: Partial<SimpleAlertOptions>, context?: ChunkContext): Promise<boolean> {
    try {
      const result = await this.openAlertDialog({ options: SimpleUIService.withDefaults(options) });
      return result !== undefined && result !== null;
    } catch (error: any) {
      console.error('Error showing alert:', error);
      if (context) {
        context.addMessage(`Error showing alert: ${error.message}`, 'danger');
        context.setChunkResultStatus("error");
      }
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  /**
   * Resolves with the entered text, or null on cancel. null (not undefined)
   * is what both step bridges map to a real null / None; undefined becomes
   * True in Python, as it does for void calls.
   */
  public async showTextPrompt(
    params: StepParams['EHQ.showTextPrompt'],
    context?: ChunkContext,
  ): Promise<string | null> {
    try {
      const options = SimpleUIService.withDefaults(params);
      return (await this.openAlertDialog({ options, promptForInput: true })) ?? null;
    } catch (error: any) {
      console.error('Error showing text prompt:', error);
      if (context) {
        context.addMessage(`Error showing text prompt: ${error.message}`, 'danger');
        context.setChunkResultStatus("error");
      }
      throw error; // Rethrow to allow try/catch blocks to work
    }
  }

  /**
   * Get job arguments (webhook payload).
   * In enoch/headless, returns the webhook payload for the current job.
   * In ehq-web, returns the run inputs from the variable_schema dialog for the
   * current workflow (EHQ-388); otherwise the legacy `job_arguments` workflow
   * variable when one is set by hand; otherwise an empty object.
   */
  public getJobArguments(
    _params?: StepParams['EHQ.getJobArguments'],
    _context?: ChunkContext,
  ): Record<string, any> {
    return SimpleUIService.resolveJobArguments(this.notebookVariablesService);
  }

  /**
   * Resolution shared by every editor runtime (Python bridge, QuickJS, EHQ
   * method): run inputs first, then the deprecated `job_arguments` variable.
   */
  static resolveJobArguments(
    source:
      | {
          getRunJobArguments?: () => Record<string, unknown> | null;
          variableList?: Array<{ name?: string; value?: unknown }>;
          variableList$?: () => Array<{ name?: string; value?: unknown }>;
        }
      | null
      | undefined,
  ): Record<string, any> {
    const runArgs = source?.getRunJobArguments?.();
    if (runArgs) {
      return { ...runArgs };
    }
    const list =
      typeof source?.variableList$ === 'function'
        ? source.variableList$()
        : source?.variableList;
    const fromVariable = SimpleUIService.resolveJobArgumentsFromVariables(list);
    if (Object.keys(fromVariable).length > 0) {
      SimpleUIService.warnJobArgumentsVariableDeprecated();
    }
    return fromVariable;
  }

  private static warnedJobArgumentsVariable = false;

  /** One console warning per page load for the legacy variable path. */
  static warnJobArgumentsVariableDeprecated(): void {
    if (SimpleUIService.warnedJobArgumentsVariable) {
      return;
    }
    SimpleUIService.warnedJobArgumentsVariable = true;
    console.warn(
      '[getJobArguments] Reading the "job_arguments" workflow variable is deprecated (EHQ-388). ' +
        "Declare the inputs in the workflow's variable schema instead; Run asks for them " +
        'and getJobArguments() returns them, as enoch does for routine runs.',
    );
  }

  /** Test hook: re-arm the one-time deprecation warning. */
  static resetJobArgumentsDeprecationWarning(): void {
    SimpleUIService.warnedJobArgumentsVariable = false;
  }

  /**
   * Parse mock webhook/job args from the legacy `job_arguments` workflow
   * variable (deprecated by EHQ-388; schema run inputs take precedence).
   */
  static resolveJobArgumentsFromVariables(
    variableList: Array<{ name?: string; value?: unknown }> | null | undefined,
  ): Record<string, any> {
    const entry = variableList?.find(v =>
      SimpleUIService.isJobArgumentsVariableName(v.name),
    );
    if (entry?.value == null || entry.value === '') {
      return {};
    }

    const parsed = SimpleUIService.parseJobArgumentsValue(entry.value);
    if (Object.keys(parsed).length === 0) {
      console.warn(
        '[getJobArguments] A "job_arguments" workflow variable exists but its value is not a JSON object. ' +
          'Paste the contents of scripts/gcg-job-args.json into the variable value field.',
      );
    }
    return parsed;
  }

  /** True when the workflow variable name is the mock webhook payload slot. */
  static isJobArgumentsVariableName(name?: string): boolean {
    const normalized = (name ?? '').trim().toLowerCase();
    return normalized === 'job_arguments' || normalized === 'jobarguments';
  }

  /** Normalize a workflow variable value into a job-arguments object. */
  static parseJobArgumentsValue(raw: unknown): Record<string, any> {
    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
      return raw as Record<string, any>;
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) {
        return {};
      }
      if (GeneralHelpers.isJSON(trimmed)) {
        const parsed = GeneralHelpers.jsonParse<Record<string, any>>(trimmed);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed;
        }
      }
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return parsed as Record<string, any>;
        }
      } catch {
        // not JSON — ignore
      }
    }

    return {};
  }

}
